import { Button, Chip, FormControlLabel, Grid, Switch, TextField, Typography } from '@mui/material';
import { FunctionComponent, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { AnswerConfig, QuestionConfig } from '../../types/GameplayTypes';
import { calculateFixedPoints, totalPoints } from '../../util/Points';
import { QUESTION_SHARE_PARAM } from '../../util/Sharing';
import { muiStyleSheet } from '../../util/Styles';
import { useScreenIsNarrow } from '../Accessibility';
import { EditAnswerRow } from './EditAnswerRow';
import { FormDialog } from './FormDialog';
import { Metadata } from './QuestionComponents';
import { ShareQuestionButton } from './ShareUrlButton';
import { QuestionTagEntry } from './Tags';

export type EditQuestionData = Pick<QuestionConfig, 'text' | 'answers' | 'tags' | 'isActive'>;

const MAX_ANSWERS = 8;

type Props = {
	open: boolean;
	question?: QuestionConfig;
	onClose: () => void;
	onSave: (data: EditQuestionData) => Promise<void>;
};

export const EditQuestionDialog: FunctionComponent<Props> = (props) => {
	const { open, question } = props;

	const [text, setText] = useState('');
	const [answers, setAnswers] = useState<AnswerConfig[]>([{ text: '', points: 0 }]);
	const [tags, setTags] = useState<string[]>([]);
	const [isActive, setIsActive] = useState(true);
	const [showFixPreview, setShowFixPreview] = useState(false);

	const [searchParams, setSearchParams] = useSearchParams();

	const normalisedAnswers = normaliseAnswers(answers);
	const total = totalPoints(normalisedAnswers);
	const arePointsValid = total === 100;
	const fixedPoints = showFixPreview ? calculateFixedPoints(normalisedAnswers) : undefined;
	const atMaxAnswerCount = answers.length >= MAX_ANSWERS;

	// Reset form when dialog opens/closes or question changes
	useEffect(function resetForm() {
		if (open) {
			if (question) {
				setText(question.text);
				setAnswers(question.answers.length > 0 ? [...question.answers] : [{ text: '', points: 0 }]);
				setTags([...question.tags]);
				setIsActive(question.isActive);
			} else {
				setText('');
				setAnswers([{ text: '', points: 0 }]);
				setTags([]);
				setIsActive(true);
			}
		}
	}, [open, question]);

	useEffect(function updateSearchParams() {
		const nextSearchParams = new URLSearchParams(searchParams);
		if (open && question) {
			nextSearchParams.set(QUESTION_SHARE_PARAM, question.id);
		} else {
			nextSearchParams.delete(QUESTION_SHARE_PARAM);
		}
		setSearchParams(nextSearchParams);
	}, [open, question]);

	function addAnswer() {
		setAnswers([...answers, { text: '', points: 0 }]);
	}

	function fixPoints() {
		setAnswers(calculateFixedPoints(normalisedAnswers));
	}

	function validate(): { valid: true, answers: AnswerConfig[]; } | { valid: false; } {
		const validAnswers = normalisedAnswers.filter(a => a.text.trim() && (a.points > 0));
		const total = validAnswers.reduce((sum, a) => sum + a.points, 0);

		if (!text.trim()) {
			alert('Question text is required');
			return { valid: false };
		} else if (normalisedAnswers.some(a => a.text.trim() && (a.points <= 0))) {
			alert('All answers must have at least one point');
			return { valid: false };
		} else if (validAnswers.empty()) {
			alert('At least one answer with text is required');
			return { valid: false };
		} else if (total !== 100) {
			alert(`Point values must sum to 100 (currently ${total}). Use the Fix Points button to adjust.`);
			return { valid: false };
		} else {
			return { valid: true, answers: validAnswers };
		}
	}

	async function submit() {
		const validation = validate();
		if (validation.valid) {
			// Sort answers by points descending
			const sortedAnswers = validation.answers.sort((a, b) => b.points - a.points);

			await props.onSave({
				text: text.trim(),
				answers: sortedAnswers,
				tags,
				isActive,
			});
		}
	}

	/** Map empty points inputs from NaN to 0 */
	function normaliseAnswers(answers: AnswerConfig[]) {
		return answers.map(a => ({ ...a, points: Number.isFinite(a.points) ? a.points : 0 }));
	}

	return <FormDialog title={question ? 'Edit Question' : 'Create Question'}
		open={props.open} onClose={props.onClose} submit={submit}
		extraAction={question && <ShareQuestionButton question={question} />}
	>
		<Grid container columns={12} rowSpacing={2} columnSpacing={1} sx={{ marginTop: 2 }}>
			<Grid size={12}>
				<TextField label="Question" fullWidth required multiline rows={2}
					value={text} onChange={(e) => setText(e.target.value)} />
			</Grid>

			<Grid size={12}>
				<Typography variant="subtitle1">Answers</Typography>
			</Grid>

			{answers.map((_answer, index) =>
				<EditAnswerRow index={index} key={`answer-${index}`}
					answers={answers} setAnswers={setAnswers}
					withFixedPoints={fixedPoints?.[index]} />)}

			<Grid size={7}>
				<Button size="small" onClick={addAnswer} disabled={atMaxAnswerCount}>Add Answer</Button>
				{atMaxAnswerCount &&
					<Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
						Maximum of {MAX_ANSWERS} answers
					</Typography>}
			</Grid>
			<Grid size={1}><Typography variant='caption'>Total:</Typography></Grid>
			<Grid size={2}><PointsTotalChip points={total} /></Grid>
			<Grid size={2}>
				{arePointsValid ||
					<Button size="small"
						onClick={fixPoints}
						onMouseEnter={() => setShowFixPreview(true)}
						onMouseLeave={() => setShowFixPreview(false)}>
						Fix Points
					</Button>}
			</Grid>

			<Grid size={12}>
				<QuestionTagEntry tags={tags} setTags={setTags} />
			</Grid>

			<Grid size={2}>
				<FormControlLabel
					control={<Switch checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />}
					label="Active"
				/>
			</Grid>
			<Grid size={8} offset={2} sx={styles.metadata}>{question && <Metadata {...question} />}</Grid>
		</Grid>
	</FormDialog >;
};

const PointsTotalChip: FunctionComponent<{ points: number; }> = (props) => {
	const isValid = props.points === 100;
	const isNarrow = useScreenIsNarrow();

	return <Chip label={`${props.points}${isNarrow ? '' : '/100'}`}
		color={isValid ? 'success' : 'error'} size="small" sx={styles.pointsTotal} />;
};

const styles = muiStyleSheet({
	metadata: {
		textAlign: 'right',
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'flex-end',
	},
	pointsTotal: {
		marginLeft: 1,
		minWidth: '3rem',
	}
});